import type { ApolloError } from "@apollo/client";
import type { ColumnDef } from "@tanstack/react-table";
import isNil from "lodash/isNil";

import type { IIPRootAttr, IToePortAttr, IToePortData } from "./types";

import { editableBePresentFormatter } from "../formatters/editable-be-present";
import { markSeenFirstTimeBy } from "../utils";
import { filterColumns } from "features/toes-ports/utils";
import type { GetToePortsQueryVariables } from "gql/graphql";
import { formatDate } from "utils/date";
import { formatBoolean, formatStatus, jsxStatus } from "utils/format-helpers";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";
import { translate } from "utils/translations/translate";

const isEqualRootId = (root: IIPRootAttr | null, rootId: string): boolean => {
  if (isNil(root)) return rootId === "";

  return root.id === rootId;
};

const formatToePortData = (toePortAttr: IToePortAttr): IToePortData => ({
  ...toePortAttr,
  attackedAt: toePortAttr.attackedAt ?? "-",
  bePresentUntil: toePortAttr.bePresentUntil ?? "-",
  firstAttackAt: toePortAttr.firstAttackAt ?? "-",
  id: crypto.randomUUID(),
  markedSeenFirstTimeBy: markSeenFirstTimeBy(toePortAttr.seenFirstTimeBy),
  rootId: isNil(toePortAttr.root) ? "" : toePortAttr.root.id,
  rootNickname: isNil(toePortAttr.root) ? "" : toePortAttr.root.nickname,
  seenAt: toePortAttr.seenAt ?? "-",
});

const handleUpdateToePortError = (errors: ApolloError): void => {
  errors.graphQLErrors.forEach((error): void => {
    switch (error.message) {
      case "Exception - The toe port is not present":
        msgError(translate.t("group.toe.ports.alerts.nonPresent"));
        break;
      case "Exception - The toe port has been updated by another operation":
        msgError(translate.t("group.toe.ports.alerts.alreadyUpdate"));
        break;
      default:
        msgError(translate.t("groupAlerts.errorTextsad"));
        Logger.warning("An error occurred updating the toe port", error);
    }
  });
};

const getColumns = (
  canUpdateToePort: boolean,
  isInternal: boolean,
  getToePortsVariables: GetToePortsQueryVariables,
  handleUpdateToePortBePresent: (row: IToePortData) => Promise<void>,
): ColumnDef<IToePortData>[] =>
  filterColumns(
    [
      {
        accessorKey: "rootNickname",
        header: translate.t("group.toe.ports.root"),
        id: "root",
      },
      {
        accessorKey: "address",
        header: translate.t("group.toe.ports.address"),
      },
      {
        accessorKey: "port",
        header: translate.t("group.toe.ports.port"),
      },
      {
        accessorFn: (row: IToePortData): string =>
          formatStatus(row.hasVulnerabilities),
        cell: (cell): JSX.Element => jsxStatus(String(cell.getValue())),
        header: String(translate.t("group.toe.ports.status")),
        id: "has_vulnerabilities",
      },
      {
        accessorKey: "seenAt",
        cell: (cell): string => formatDate(cell.getValue<string>()),
        header: translate.t("group.toe.ports.seenAt"),
        id: "seen_at",
      },
      {
        accessorFn: (row: IToePortData): string => {
          return formatBoolean(row.bePresent);
        },
        cell: (cell): JSX.Element =>
          editableBePresentFormatter(
            canUpdateToePort && isInternal,
            handleUpdateToePortBePresent,
            cell.row.original,
          ),
        header: translate.t("group.toe.ports.bePresent"),
        id: "be_present",
      },
      {
        accessorKey: "attackedAt",
        cell: (cell): string => formatDate(cell.getValue<string>()),
        header: translate.t("group.toe.ports.attackedAt"),
        id: "attacked_at",
      },
      {
        accessorKey: "attackedBy",
        header: translate.t("group.toe.ports.attackedBy"),
        id: "attacked_by",
      },
      {
        accessorKey: "firstAttackAt",
        cell: (cell): string => formatDate(cell.getValue<string>()),
        header: translate.t("group.toe.ports.firstAttackAt"),
        id: "first_attack_at",
      },
      {
        accessorKey: "seenFirstTimeBy",
        header: translate.t("group.toe.ports.seenFirstTimeBy"),
        id: "seen_first_time_by",
      },
      {
        accessorKey: "bePresentUntil",
        cell: (cell): string => formatDate(cell.getValue<string>()),
        header: translate.t("group.toe.ports.bePresentUntil"),
        id: "be_present_until",
      },
    ],
    isInternal,
    getToePortsVariables,
  );

export {
  isEqualRootId,
  formatToePortData,
  getColumns,
  handleUpdateToePortError,
};
